import { useState } from "react";
import { createRoute, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, Tag, TriangleAlert } from "lucide-react";

import { rootRoute } from "./root.tsx";
import { SubPage } from "../components/layout.tsx";
import { Button, Empty, ErrorBlock, Input, Price, Spinner } from "../components/ui.tsx";
import { useIdentities, useMe } from "../api/queries.ts";
import { orgFetch, toCents } from "../api/org-client.ts";
import { useIsDesktop } from "../lib/viewport.ts";
import { DesktopMyPricing } from "./desktop/my-pricing.tsx";

/**
 * 我的定价 —— **商家的第二种身份：经营者**。
 *
 * 🔴 商家不逛自己的店（店是给客户逛的）。他在后台看的是这张表：
 *    每个规格 **我进多少 → 我卖多少 → 我赚多少**，可以逐个改。
 *
 * ⚠️ 这里的价按**我自己**的链算，跟前台"访问者看到什么价"是两回事。
 *    出参里没有上级的成本 —— 服务端已经裁剪掉了。
 */

export interface PricingSku {
  skuId: number;
  specs: Record<string, string>;
  unit: string;
  stock: number;
  cost: number;
  sell: number;
  margin: number;
  rate: number;
  custom: boolean;
  warning?: string;
  /**
   * 🔴 **平台专属：给下级的批发价。**
   *    老板：「陈总的店和平台定价是两回事」。平台是唯一一个
   *    "卖给下级"和"卖给消费者"不是一个数的节点 ——
   *    `sell` 是他**店里卖给客户**的价，这个是**给下级进货**的价。
   *    别的商家没有这个字段（他们的售价对下级和对客户是同一个数）。
   */
  wholesaleSell?: number;
  wholesaleCustom?: boolean;
}
export interface PricingProduct {
  productId: number;
  title: string;
  imageUrl: string | null;
  skus: PricingSku[];
}

/**
 * 一个可改的价。
 *
 * 🔴 **平台有两个，别人只有一个。**
 *    老板：「陈总的店和平台定价是两回事」——
 *    `chain` 是"下级按它进货"，`shop` 是"我店里卖给客户多少"。
 *    别人只有 `chain`：他的售价对下级和对客户就是同一个数。
 *
 * ⚠️ **标签必须写清是哪一个。** 早先这两个数挤在一行，页面上写着「我卖 ¥238」，
 *    改下去改的却是批发价 —— 陈总以为给自己店打了个九折，
 *    实际把每个下级的进货价从 ¥105.60 抬到 ¥214.20，下级当场亏本卖。
 */
function PriceLine({
  sku,
  orgId,
  kind,
  label,
  hint,
  value,
  custom,
}: {
  sku: PricingSku;
  orgId: number;
  kind: "chain" | "shop";
  label: string;
  hint: string;
  value: number;
  custom: boolean;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const done = () => {
    setEditing(false);
    void qc.invalidateQueries({ queryKey: ["my-pricing", orgId] });
    /** ⚠️ 前台的商品价是另一个查询 —— 不作废的话他去店里一看还是老价，会以为没保存上。 */
    void qc.invalidateQueries({ queryKey: ["products"] });
  };

  const save = useMutation({
    mutationFn: (price: number) =>
      orgFetch("/pricing/sku", orgId, {
        method: "POST",
        body: JSON.stringify({ skuId: sku.skuId, price, kind }),
      }),
    onSuccess: done,
  });

  /** 清除我定的价 → 这个规格回落到默认值（批量加价率 / 平台价）。 */
  const clear = useMutation({
    mutationFn: () =>
      orgFetch(`/pricing/sku/${sku.skuId}?kind=${kind}`, orgId, { method: "DELETE" }),
    onSuccess: done,
  });

  return (
    <div className="mt-2 first:mt-1">
      <div className="flex items-baseline gap-2">
        <span className="text-xs text-ink-500">{label}</span>
        <span className="text-sm">
          <Price cents={value} />
        </span>
        {custom && (
          <span className="rounded bg-brand-50 px-1.5 py-0.5 text-[11px] text-brand-700">
            单独定价
          </span>
        )}
        {!editing && (
          <button
            className="ml-auto shrink-0 text-xs text-brand-600"
            onClick={() => {
              setDraft((value / 100).toFixed(2));
              setEditing(true);
            }}
          >
            改
          </button>
        )}
      </div>
      <p className="mt-0.5 text-[11px] text-ink-400">{hint}</p>

      {editing && (
        <div className="mt-2">
          <div className="flex gap-2">
            <Input
              inputMode="decimal"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="flex-1"
            />
            <span className="self-center text-sm text-ink-500">元 / {sku.unit}</span>
            <Button
              size="sm"
              loading={save.isPending}
              onClick={() => {
                const cents = toCents(draft);
                if (cents !== null) save.mutate(cents);
              }}
            >
              保存
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              取消
            </Button>
          </div>
          {/* ⚠️ 低于进货价服务端会挡下来，这里先提示，省得他填完才被拒 */}
          <p className="mt-1.5 text-xs text-ink-500">
            不能低于你的进货价 <Price cents={sku.cost} />
          </p>
          {custom && (
            <button
              className="mt-1.5 text-xs text-ink-500 underline"
              onClick={() => clear.mutate()}
              disabled={clear.isPending}
            >
              {kind === "shop" ? "恢复成按平台价卖" : "恢复成按批量加价率"}
            </button>
          )}
          {save.error && <ErrorBlock error={save.error} />}
          {clear.error && <ErrorBlock error={clear.error} />}
        </div>
      )}
    </div>
  );
}

function SkuRow({ sku, orgId }: { sku: PricingSku; orgId: number }) {
  const specText = Object.values(sku.specs).join(" · ") || "默认";
  /** 🔴 只有平台会带这个字段 —— 带了就说明他有两个价要分开管。 */
  const isPlatform = sku.wholesaleSell !== undefined;

  return (
    <div className="border-t border-neutral-100 px-4 py-3 first:border-0">
      <p className="text-sm">{specText}</p>
      <p className="mt-1 text-xs text-ink-500">
        我买 <Price cents={sku.cost} />
        {!isPlatform && (
          <>
            <span className="ml-1.5 text-brand-600">
              赚 <Price cents={sku.margin} />
            </span>
            <span className="ml-1">（{(sku.rate * 100).toFixed(1)}%）</span>
          </>
        )}
      </p>
      {sku.warning && (
        <p className="mt-1 flex items-center gap-1 text-xs text-amber-600">
          <TriangleAlert className="size-3" />
          {sku.warning}
        </p>
      )}

      {isPlatform ? (
        <>
          <PriceLine
            sku={sku}
            orgId={orgId}
            kind="shop"
            label="我店里卖"
            hint="没有上级的客人（游客、自己注册的人）付这个价。留空就按后台定的平台价。"
            value={sku.sell}
            custom={sku.custom}
          />
          <PriceLine
            sku={sku}
            orgId={orgId}
            kind="chain"
            label="给下级的批发价"
            hint="经销商 / 分销商按这个价从你这里进货。改它会连带改掉他们的成本。"
            value={sku.wholesaleSell!}
            custom={sku.wholesaleCustom ?? false}
          />
        </>
      ) : (
        <PriceLine
          sku={sku}
          orgId={orgId}
          kind="chain"
          label="我卖"
          hint="你的客户付这个价，你的下级也按这个价进货。"
          value={sku.sell}
          custom={sku.custom}
        />
      )}
    </div>
  );
}

/**
 * 上游调价提醒。
 *
 * 🔴 **这是定死了售价的商家唯一能发现"我在亏本卖"的地方。**
 *    按加价率卖的会自动跟涨，定死价的不会 —— 上游一涨他就一单亏一单，
 *    不摆到他改价的这张表上面，他要到对账时才发现。
 *
 * ⚠️ 放在「我的定价」页顶部而不是单独开一页：看到提醒的下一步动作
 *    就是改价，两件事之间不该隔一次跳转。
 */
export interface PriceNotice {
  id: number;
  skuId: number;
  oldCost: number;
  newCost: number;
  currentPrice: number;
  belowCost: boolean;
  effectiveAt: string;
  acknowledgedAt: string | null;
  title: string;
  specs: Record<string, string>;
}

export function hoursLeft(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "已生效";
  const h = Math.ceil(ms / 3_600_000);
  /** ⚠️ 默认缓冲就是 24 小时，写成"1 天后"看着像还早 —— 两天以内一律按小时说。 */
  return h > 48 ? `${Math.ceil(h / 24)} 天后生效` : `${h} 小时后生效`;
}

function PriceNotices({ orgId }: { orgId: number }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["price-notices", orgId],
    queryFn: () => orgFetch<{ items: PriceNotice[]; unread: number }>("/price-notices", orgId),
  });

  const ack = useMutation({
    mutationFn: (ids: number[]) =>
      orgFetch<{ acknowledged: number }>("/price-notices/ack", orgId, {
        method: "POST",
        body: JSON.stringify({ ids }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["price-notices", orgId] });
      /** 缓冲期内看到的还是旧进货价，确认后顺手把定价表也刷一下。 */
      qc.invalidateQueries({ queryKey: ["my-pricing"] });
    },
  });

  const pending = (q.data?.items ?? []).filter((n) => !n.acknowledgedAt);
  if (pending.length === 0) return null;

  const losing = pending.filter((n) => n.belowCost);

  return (
    <div className="mt-2 bg-white">
      <div className="flex items-center gap-2 px-4 pt-3">
        <BellRing className="size-4 text-amber-600" />
        <p className="flex-1 text-sm font-medium">上游调价（{pending.length}）</p>
        <button
          className="text-xs text-ink-500 underline"
          onClick={() => ack.mutate(pending.map((n) => n.id))}
          disabled={ack.isPending}
        >
          全部知道了
        </button>
      </div>

      {/* ⚠️ 倒挂的单独说一句 —— 这是要立刻动手的，不能跟普通涨价混在一起 */}
      {losing.length > 0 && (
        <p className="mx-4 mt-2 rounded bg-red-50 px-3 py-2 text-xs text-red-700">
          有 <strong>{losing.length}</strong> 个规格<strong>已经卖一单亏一单</strong>
          ，涨价生效前改掉你的售价。
        </p>
      )}

      <div className="mt-1">
        {pending.map((n) => (
          <div key={n.id} className="border-b border-neutral-100 px-4 py-3 last:border-0">
            <p className="text-sm leading-snug">
              {n.title}
              <span className="ml-1 text-xs text-ink-500">
                {Object.values(n.specs).join(" · ")}
              </span>
            </p>
            <p className="mt-1 text-xs text-ink-500">
              进货价 <Price cents={n.oldCost} /> → <Price cents={n.newCost} />
              <span className="ml-1.5 rounded bg-neutral-100 px-1.5 py-0.5">
                {hoursLeft(n.effectiveAt)}
              </span>
            </p>
            {n.belowCost ? (
              <p className="mt-1 flex items-center gap-1 text-xs text-red-600">
                <TriangleAlert className="size-3.5" />
                你卖 <Price cents={n.currentPrice} />，生效后每单亏{" "}
                <Price cents={n.newCost - n.currentPrice} />
              </p>
            ) : (
              <p className="mt-1 text-xs text-ink-500">
                你卖 <Price cents={n.currentPrice} />，生效后还赚{" "}
                <Price cents={n.currentPrice - n.newCost} />
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function MyPricing() {
  const search = useSearch({ from: "/my-pricing" }) as { orgId?: number };
  const { data: me, isPending: mePending } = useMe();
  const { data: identities } = useIdentities(Boolean(me));

  /**
   * 能在这里定价的是**链上的转售方**：平台、经销商、分销商。
   *
   * 🔴 平台也在其中 —— 老板从供应商按供货价拿货、加价卖给下级，
   *    他的「经营者」身份就落在这一页。
   *    供应商不在其中：他定的是供货价，走商品编辑那套界面。
   */
  const orgs = (identities?.orgs ?? []).filter(
    (o) =>
      o.status === "active" &&
      (o.type === "platform" || o.type === "dealer" || o.type === "distributor"),
  );
  const [picked, setPicked] = useState<number | null>(search.orgId ?? null);
  const orgId = picked ?? orgs[0]?.id ?? null;
  const org = orgs.find((o) => o.id === orgId);

  const [keyword, setKeyword] = useState("");

  const list = useQuery({
    queryKey: ["my-pricing", orgId, keyword],
    queryFn: () =>
      orgFetch<{ items: PricingProduct[]; nextCursor: string | null }>(
        `/pricing/catalog?limit=50${keyword ? `&keyword=${encodeURIComponent(keyword)}` : ""}`,
        orgId!,
      ),
    enabled: Boolean(orgId),
  });

  if (mePending) {
    return (
      <SubPage title="我的定价">
        <Spinner />
      </SubPage>
    );
  }

  if (!me || orgs.length === 0) {
    return (
      <SubPage title="我的定价">
        <Empty title="你名下没有可定价的店" hint="定价是商家功能，联系上级商家或平台开通" />
      </SubPage>
    );
  }

  return (
    <SubPage title="我的定价">
      {orgs.length > 1 && (
        <div className="flex gap-2 overflow-x-auto bg-white px-4 py-2.5">
          {orgs.map((o) => (
            <button
              key={o.id}
              onClick={() => setPicked(o.id)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-sm ${
                o.id === orgId ? "bg-brand-600 text-white" : "bg-neutral-100 text-ink-500"
              }`}
            >
              {o.name}
            </button>
          ))}
        </div>
      )}

      {orgId && <PriceNotices orgId={orgId} />}

      <div className="mt-2 bg-brand-50 px-4 py-2.5 text-xs text-brand-700">
        <p className="flex items-center gap-1.5">
          <Tag className="size-3.5" />
          这是「{org?.name}」<strong>卖给客户</strong>的价
        </p>
        <p className="mt-0.5">
          🔴 <strong>卖多少钱由你定</strong> —— 在进货价和指导零售价之间，你说了算。
          批量加价率只是还没单独定价时的默认值；这里改过的规格
          <strong>不会</strong>被批量加价率覆盖。
        </p>
      </div>

      <div className="bg-white px-4 py-2.5">
        <Input
          placeholder="搜商品名"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />
      </div>

      {list.isPending ? (
        <Spinner />
      ) : list.error ? (
        <ErrorBlock error={list.error} onRetry={() => list.refetch()} />
      ) : list.data.items.length === 0 ? (
        <Empty title="上游还没有可卖的商品" />
      ) : (
        <div className="mt-2">
          {list.data.items.map((p) => (
            <div key={p.productId} className="mb-2 bg-white">
              <p className="px-4 pt-3 text-sm font-medium leading-snug">{p.title}</p>
              <div className="mt-1">
                {p.skus.map((s) => (
                  <SkuRow key={s.skuId} sku={s} orgId={orgId!} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </SubPage>
  );
}

/** 手机版 / 网页版是两套页面，按视口挑一套（URL 与数据 hooks 两边共用）。 */
function MyPricingRoute() {
  return useIsDesktop() ? <DesktopMyPricing /> : <MyPricing />;
}

export const myPricingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/my-pricing",
  component: MyPricingRoute,
  validateSearch: (s: Record<string, unknown>): { orgId?: number } => ({
    orgId: s.orgId ? Number(s.orgId) : undefined,
  }),
});
